import { ApolloClient, InMemoryCache, HttpLink, ApolloLink } from "@apollo/client";
import { ErrorLink } from "@apollo/client/link/error";
import { CombinedGraphQLErrors, CombinedProtocolErrors } from "@apollo/client/errors";
import { SetContextLink } from "@apollo/client/link/context";
import { AppConfig } from "@/infrastructure/services/config";
import { createLogger, ILogger } from "@/infrastructure/services/logger";

declare module "@apollo/client" {
  namespace ApolloClient.DefaultOptions {
    namespace DeclareDefaultOptions {
      // Affects client.watchQuery() and React hooks (useQuery, useSuspenseQuery, etc.)
      interface WatchQueryInput {
        errorPolicy: "all";
      }
      // Affects client.query()
      interface QueryInput {
        errorPolicy: "all";
      }
      // Affects client.mutate()
      interface MutateInput {
        errorPolicy: "all";
      }
    }
  }
}

const GRAPHQL_BASE_URL = AppConfig.getInstance().GRAPHQL_BASE_URL;

export class ShareGraphQlAdapter {
  private client: ApolloClient | null = null;
  private shareToken: string | null = null;
  private static instance: ShareGraphQlAdapter;
  private readonly logger: ILogger = createLogger("ShareGraphQlAdapter");

  public static getInstance(): ShareGraphQlAdapter {
    if (!ShareGraphQlAdapter.instance) {
      ShareGraphQlAdapter.instance = new ShareGraphQlAdapter();
    }
    return ShareGraphQlAdapter.instance;
  }

  public setShareToken(token: string): void {
    this.shareToken = token;
    this.client = this.initializeClient();
  }

  public clearShareToken(): void {
    this.shareToken = null;
    this.client = null;
  }

  public getClient(): ApolloClient {
    if (!this.client || !this.shareToken) {
      throw new Error("Share token is not configured");
    }
    return this.client;
  }

  private initializeClient(): ApolloClient {
    const shareToken = this.shareToken;
    const httpLink = new HttpLink({ uri: GRAPHQL_BASE_URL });

    const errorLink = new ErrorLink(({ error }) => {
      if (CombinedGraphQLErrors.is(error)) {
        for (const { message, locations, path } of error.errors) {
          this.logger.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
        }
      } else if (CombinedProtocolErrors.is(error)) {
        for (const { message, extensions } of error.errors) {
          this.logger.error(`[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`);
        }
      } else {
        this.logger.error(`[Network error]: ${error.message}`);
      }
    });

    const authLink = new SetContextLink(prevContext => ({
      ...prevContext,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Share-Token": shareToken,
      },
    }));

    return new ApolloClient({
      link: ApolloLink.from([errorLink, authLink, httpLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: { errorPolicy: "all" },
        query: { errorPolicy: "all" },
        mutate: { errorPolicy: "all" },
      },
    });
  }
}
